/**
 * Conversational Lead Capture Chat Widget
 * 
 * Pattern:
 * 1. Works anonymously - no email required upfront
 * 2. Answers product/security/architecture questions helpfully
 * 3. After providing value (3+ exchanges), offers to email/PDF the conversation
 * 4. Tracks: questions asked, topic clusters, intent score
 * 5. Email becomes optional, not mandatory
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  MessageCircle, X, Send, Sparkles, Mail, FileText,
  Minimize2, Maximize2, Bot, User, Download, ChevronDown
} from 'lucide-react';
import DOMPurify from 'dompurify';
import analyticsService from '../services/analyticsService';

// Topic keywords with comprehensive coverage
const TOPIC_KEYWORDS = {
  // Specific compliance topics (highest priority)
  hipaa: ['hipaa', 'hippa', 'phi', 'protected health', 'healthcare', 'medical', 'patient', 'baa', 'health insurance', 'hospital', 'clinic', 'ehr', 'emr', 'covered entity', 'health data', 'health information'],
  gdpr: ['gdpr', 'dpa', 'data protection act', 'right to be forgotten', 'data subject', 'eu privacy', 'european', 'eu compliance', 'europe', 'dpo', 'data protection officer', 'eu data', 'data residency'],
  soc2: ['soc2', 'soc 2', 'type ii', 'type 2', 'aicpa', 'trust services', 'soc report', 'attestation'],
  iso27001: ['iso 27001', 'iso27001', 'isms', 'information security management'],

  // Industry-specific (avoid matching common words like "legal")
  industries: ['industry', 'vertical', 'financial services', 'banking', 'law firm', 'attorney', 'call center', 'bpo company', 'retail', 'manufacturing', 'government', 'public sector', 'education sector', 'university', 'nonprofit', 'insurance company', 'telecom', 'pharma', 'pharmaceutical'],

  // Insider threat (specific topic)
  insider_threat: ['insider threat', 'insider risk', 'data exfiltration', 'data theft', 'employee theft', 'flight risk', 'leaking data', 'stealing data', 'unauthorized access', 'malicious insider', 'departing employee', 'terminated employee', 'disgruntled', 'sabotage'],

  // Stealth/visibility mode (avoid "employees know" - that's privacy)
  stealth: ['stealth', 'hidden monitoring', 'invisible', 'tray icon', 'silent mode', 'covert', 'undetectable', 'is it visible', 'hidden from'],

  // Legal considerations
  legal: ['is it legal', 'legal to monitor', 'legally monitor', 'ecpa', 'ccpa', 'labor law', 'employment law', 'union approval', 'works council', 'notification requirement', 'disclosure requirement', 'legal requirement'],

  // ROI and business value (be careful - "roi" matches inside "android")
  roi: [' roi', 'roi ', 'return on investment', 'business value', 'case study', 'success story', 'productivity increase', 'payback', 'business case', 'case studies', 'success stories'],

  // Mobile monitoring (check before ROI to catch "android" before " roi")
  mobile: ['mobile device', 'android', 'ios', 'iphone', 'ipad', 'tablet', 'byod', 'bring your own', 'smartphone', 'gps', 'location tracking', 'mobile monitoring', 'mobile app', 'mobile agent'],

  // Time tracking specific
  timetracking: ['time track', 'timesheet', 'clock in', 'clock out', 'attendance', 'hours worked', 'overtime', 'billable hour', 'time theft', 'buddy punching', 'break time', 'idle time', 'active time', 'punch clock'],

  // Reports specific (avoid "analytics" - too generic)
  reports: ['report', 'dashboard', 'export', 'csv', 'excel', 'pdf', 'visualization', 'chart', 'graph', 'metric', 'kpi', 'weekly report', 'monthly report', 'custom report'],

  // Privacy (check before features to catch "monitor" questions about employee awareness)
  privacy: ['privacy', 'consent', 'employee rights', 'transparent', 'personal data', 'minimize', 'anonymize', 'pseudonymize', 'data retention', 'delete data', 'erase', 'what do you track', 'what do you collect', 'spy', 'surveillance', 'intrusive', 'creepy', 'being monitored', 'being tracked', 'employees know', 'employees see', 'data collect', 'collect data', 'you collect', 'do you collect', 'opt out', 'spyware'],

  // Core topics
  pricing: ['price', 'cost', 'tier', 'billing', 'subscription', 'how much', 'affordable', 'budget', 'pay', 'payment', 'discount', 'annual', 'monthly', 'per user', 'per seat', 'quote', 'pricing', 'expensive', 'cheap', 'starter plan', 'enterprise plan', 'professional plan'],
  security: ['security', 'encryption', 'encrypt', 'secure', 'breach', 'vulnerability', 'penetration', 'hack', 'attack', 'firewall', 'mfa', 'two factor', '2fa', 'authentication', 'authorization', 'access control', 'rbac', 'sso', 'single sign', 'cyber'],
  features: ['feature', 'capability', 'screenshot', 'what can', 'does it', 'functionality', 'ueba', 'dlp', 'anomaly', 'risk', 'productivity', 'website', 'keystroke', 'screen recording', 'video', 'ocr', 'how does', 'ai insight', 'ai analytics', 'record the screen'],
  deployment: ['install', 'deploy', 'agent', 'setup', 'configure', 'onboard', 'requirement', 'rollout', 'implement', 'download', 'msi', 'pkg', 'dmg', 'exe', 'gpo', 'sccm', 'intune', 'jamf', 'mdm', 'silent install', 'mass deploy', 'on-premise', 'on premise', 'self-hosted', 'cloud', 'saas', 'hosting', 'windows', 'mac', 'linux', 'macos'],
  integration: ['integrate', 'api', 'webhook', 'siem', 'okta', 'azure ad', 'slack', 'teams', 'workday', 'connect', 'splunk', 'sentinel', 'jira', 'servicenow', 'bamboohr', 'google workspace', 'active directory', 'ldap', 'scim', 'rest api', 'sdk', 'third party', '3rd party'],
  comparison: ['compare', 'vs', 'versus', 'alternative', 'competitor', 'teramind', 'hubstaff', 'activtrak', 'better than', 'difference', 'veriato', 'time doctor', 'desktime', 'interguard', 'why vivyawatch', 'why choose', 'switch from', 'instead of', 'should i choose', 'choose you'],
  remote: ['remote', 'hybrid', 'work from home', 'wfh', 'distributed', 'global team', 'timezone', 'time zone', 'offshore', 'outsourc', 'virtual', 'flexible work', 'async', 'remote team', 'remote employee', 'remote worker', 'works from home', 'working from home', 'home office'],
  support: ['customer support', 'help desk', 'customer service', 'customer success', 'csm', 'sla', 'response time', 'ticket', 'phone support', 'chat support', 'email support', 'documentation', 'docs', 'knowledge base', 'onboarding help', 'do you offer training'],
  demo: ['demo', 'demonstration', 'see it', 'show me', 'walkthrough', 'presentation', 'meeting', 'call', 'sales call', 'book a demo', 'schedule', 'tour', 'see how'],
  trial: ['trial', 'try', 'test drive', 'evaluat', 'pilot', 'poc', 'proof of concept', 'free', 'no credit card', 'sign up', 'get started', 'start using', 'try it'],
  about: ['what is vivya', 'company', 'founded', 'mission', 'tell me about', 'overview']
};

// Priority order - specific topics first, then general
const TOPIC_PRIORITY = [
  // Compliance (most specific)
  'hipaa', 'gdpr', 'soc2', 'iso27001',
  // Industry-specific
  'industries',
  // High purchase intent
  'pricing', 'comparison', 'trial', 'demo', 'roi',
  // Specific topics (before general features)
  'insider_threat', 'stealth', 'legal', 'mobile', 'timetracking', 'reports',
  // Remote before integration (to catch "work for remote teams" before "workday")
  'remote',
  // Implementation
  'deployment', 'integration',
  // Privacy BEFORE features
  'privacy',
  // Product info
  'security', 'features',
  // General
  'support', 'about'
];

// Grounded responses for common questions
const KNOWLEDGE_BASE = {
  // Specific compliance frameworks
  hipaa: `VivyaWatch provides comprehensive HIPAA compliance for healthcare organizations:

**Technical Safeguards:**
• **PHI Protection**: Automatic detection and redaction of Protected Health Information
• **Encryption**: AES-256 encryption at rest, TLS 1.3 in transit for all PHI
• **Access Controls**: Role-based access with PHI-specific permission levels
• **Audit Logging**: Complete 6-year audit trail for all PHI access (as required by HIPAA)
• **Data Integrity**: Cryptographic checksums to detect unauthorized modifications

**Administrative Safeguards:**
• **BAA Support**: Business Associate Agreement management built-in
• **Risk Assessment**: Automated HIPAA risk analysis tools
• **Breach Notification**: 72-hour breach detection and notification workflows
• **Minimum Necessary**: Data minimization controls to limit PHI exposure

**Physical Safeguards:**
• **Data Residency**: US-only data centers with SOC 2 certification
• **Device Security**: Endpoint security posture monitoring

We offer a **HIPAA Compliance Pack add-on** (+$3/user/month) for full compliance features. [Book a HIPAA demo](/contact)`,

  gdpr: `VivyaWatch is fully GDPR compliant for EU operations:

**Data Subject Rights (Articles 12-23):**
• **Right to Access**: Employee portal to view all collected data
• **Right to Erasure**: One-click data deletion with audit trail
• **Right to Portability**: Export all data in standard formats
• **Right to Rectification**: Employees can request corrections

**Lawful Processing (Article 6):**
• **Consent Management**: Built-in consent collection and tracking
• **Legitimate Interest**: Documentation tools for LIA
• **Purpose Limitation**: Strict data use controls

**Privacy by Design (Article 25):**
• **Data Minimization**: Collect only what's needed
• **Privacy Zones**: Exclude personal apps/websites
• **Pseudonymization**: Optional anonymization features
• **DPIA Built-in**: Privacy Impact Assessments for every policy

**Cross-Border Transfers:**
• **EU Data Centers**: Data residency in Frankfurt, Ireland
• **SCCs**: Standard Contractual Clauses supported
• **DPO Tools**: Data Protection Officer dashboard

We're the only monitoring solution with built-in DPIA automation.`,

  soc2: `VivyaWatch maintains SOC 2 Type II certification:

**Trust Service Criteria:**
• **Security**: AES-256 encryption, MFA, RBAC, vulnerability scanning
• **Availability**: 99.9% SLA, multi-region deployment, disaster recovery
• **Processing Integrity**: Data validation, audit logs, change management
• **Confidentiality**: Data classification, access controls, DLP
• **Privacy**: Consent management, data subject rights, retention policies

**Audit & Compliance:**
• **Annual Audits**: Third-party SOC 2 Type II audit by [Auditor Name]
• **Attestation Report**: Available under NDA for enterprise customers
• **Continuous Monitoring**: Real-time security posture tracking

**Security Controls:**
• **Penetration Testing**: Annual third-party pen testing
• **Vulnerability Scanning**: Weekly automated scans
• **Incident Response**: 24/7 security team, documented IR plan

SOC 2 report available upon request for enterprise evaluations.`,

  iso27001: `VivyaWatch is ISO 27001 certified:

**Information Security Management System (ISMS):**
• **Risk Assessment**: Systematic identification and treatment of security risks
• **Security Controls**: 114 controls across 14 domains
• **Continuous Improvement**: Annual surveillance audits

**Key Control Areas:**
• **Access Control**: Role-based permissions, MFA, SSO
• **Cryptography**: AES-256 encryption, secure key management
• **Physical Security**: SOC 2 certified data centers
• **Operations Security**: Change management, malware protection
• **Communications Security**: Network segmentation, TLS 1.3
• **Supplier Relationships**: Vendor security assessments
• **Incident Management**: 24/7 monitoring, documented response procedures
• **Business Continuity**: Disaster recovery, 99.9% uptime SLA

**Certification Details:**
• Certified by [Certification Body]
• Annual surveillance audits
• Certificate available upon request for enterprise customers

ISO 27001 certification demonstrates our commitment to information security best practices.`,

  pricing: `VivyaWatch offers transparent, competitive pricing:

**Pricing Tiers:**
• **Starter**: $5/user/month - Basic monitoring, screenshots, activity tracking
• **Professional**: $9/user/month - AI insights, advanced reports, integrations
• **Enterprise**: Custom pricing - Full compliance suite, dedicated support, on-premise

**Add-On Modules:**
• HIPAA Compliance Pack: +$3/user/month
• Advanced Insider Risk: +$2/user/month
• Burnout Prevention Suite: +$2/user/month

**Billing Options:**
• Monthly or Annual (20% discount on annual)
• No minimum users, no hidden fees
• **14-day free trial** - no credit card required

[View Full Pricing](/pricing) or [Contact Sales](/contact) for enterprise quotes.`,

  security: `VivyaWatch takes security seriously:

**Encryption:**
• **At Rest**: AES-256 encryption for all stored data
• **In Transit**: TLS 1.3 for all communications
• **Key Management**: Per-tenant encryption keys, HSM-backed

**Compliance Certifications:**
• SOC 2 Type II (Security, Availability, Confidentiality)
• ISO 27001 certified
• GDPR compliant
• HIPAA-ready (with compliance pack)

**Access Security:**
• Multi-Factor Authentication (MFA)
• Role-Based Access Control (RBAC)
• SSO integration (Okta, Azure AD, Google)
• Session management with auto-logout

**Infrastructure:**
• Cloud: AWS with multi-region redundancy
• Penetration tested annually
• 24/7 security monitoring
• Bug bounty program

[Download Security Whitepaper](/security) or [Request SOC 2 Report](/contact)`,

  privacy: `VivyaWatch is built privacy-first:

**Employee Transparency:**
• **Visibility Portal**: Employees see exactly what's tracked
• **Consent Management**: Built-in consent collection
• **Privacy Zones**: Exclude personal apps/sites from monitoring
• **Blur & Redact**: Automatically hide sensitive content

**Data Minimization:**
• Collect only what you need
• Configurable retention periods (30/60/90/custom days)
• Automatic data purging
• No keystroke logging by default

**Employee Rights:**
• Right to access their data
• Right to request corrections
• Right to data portability
• Right to be forgotten (with compliance)

**Privacy Assessments:**
• Built-in DPIA (Data Protection Impact Assessment)
• Privacy risk scoring
• Policy recommendations

We believe monitoring and privacy can coexist. See our [Privacy Commitment](/privacy-policy).`,

  deployment: `Deployment is quick and flexible:

**Agent Installation:**
• 5-minute silent install
• MSI (Windows), PKG (macOS), DEB/RPM (Linux)
• No reboot required

**Mass Deployment:**
• GPO (Group Policy)
• SCCM / ConfigMgr
• Intune / Jamf
• Ansible / Puppet / Chef

**Hosting Options:**
• **Cloud**: Fully managed, AWS infrastructure
• **On-Premise**: Self-hosted option for enterprise
• **Hybrid**: Agents on-prem, analytics in cloud

**Requirements:**
• Windows 10/11 (64-bit)
• macOS 10.15+ (Intel/Apple Silicon)
• Ubuntu 18.04+, CentOS 7+

Most customers deploy to 500+ devices in 1-2 days. [Download Deployment Guide](/docs)`,

  features: `Key VivyaWatch capabilities:

**Productivity Analytics:**
• Real-time activity tracking
• App & website categorization (productive/neutral/unproductive)
• Working hours analysis
• Focus time tracking

**Screenshot Intelligence:**
• Configurable screenshot intervals
• AI-powered OCR and content analysis
• Automatic PHI/PII redaction
• Video playback mode

**Insider Risk Detection:**
• User & Entity Behavior Analytics (UEBA)
• Anomaly detection with ML
• DLP policy enforcement
• Fake activity detection

**Compliance Suite:**
• Policy management
• Audit-ready reports
• Employee consent tracking
• Data retention controls

**AI Insights:**
• Natural language queries ("Who worked overtime last week?")
• Trend predictions
• Burnout risk indicators
• Smart recommendations

Everything is customizable per department/role with privacy zones.`,

  integration: `VivyaWatch integrates with your existing tools:

**SIEM & Security:**
• Splunk, Microsoft Sentinel, Elastic SIEM
• Sumo Logic, LogRhythm
• Custom webhook alerts

**HR & Identity:**
• Workday, BambooHR, SAP SuccessFactors
• Okta, Azure AD, Google Workspace, OneLogin
• SCIM user provisioning

**Collaboration:**
• Slack, Microsoft Teams
• Email notifications
• Custom webhooks

**Ticketing & IT:**
• Jira, ServiceNow, Zendesk
• Freshservice, PagerDuty

**API & Custom:**
• Full REST API with OpenAPI docs
• Webhook events for all actions
• SDK for custom integrations
• Bulk data export (CSV, JSON)

[View API Documentation](/docs/api)`,

  comparison: `Why VivyaWatch vs competitors:

| Feature | VivyaWatch | ActivTrak | Hubstaff | Teramind |
|---------|------------|-----------|----------|----------|
| **Privacy-First** |  Built-in |  Add-on |  Limited |  Partial |
| **AI Analytics** |  GPT-4 Powered |  Basic |  No |  Limited |
| **HIPAA Ready** |  Full Suite |  No |  No |  Partial |
| **Fake Activity Detection** |  Yes |  No |  No |  No |
| **Min Users** | None | 5 | 2 | 5 |
| **Free Trial** | 14 days | 14 days | 14 days | 7 days |

**Why Choose VivyaWatch:**
• 30-40% cheaper than Teramind
• Better privacy controls than any competitor
• AI-powered insights competitors don't have
• No minimum user requirements

[See Full Comparison](/compare)`,

  remote: `VivyaWatch is perfect for remote/hybrid teams:

**Fair Productivity Tracking:**
• Focus on outcomes, not surveillance
• Async-friendly metrics
• No micromanagement features

**Global Team Support:**
• Automatic timezone adjustment
• Multi-currency billing
• Regional data centers (US, EU, India)

**Work-Life Balance:**
• Configurable working hours
• Automatic pause during off-hours
• Burnout risk indicators
• No after-hours tracking by default

**Trust & Transparency:**
• Employee visibility portal
• Clear communication about what's tracked
• Privacy zones for personal time

Used by 500+ remote-first companies worldwide including [Customer Names].`,

  demo: `Ready to see VivyaWatch in action?

**Demo Options:**
• **Live Demo**: 30-minute personalized walkthrough with our team
• **Self-Guided**: Interactive product tour (no signup required)
• **Video Library**: Pre-recorded feature demos

**Free Trial:**
• 14 days full access
• No credit card required
• All features unlocked
• Free onboarding call included

**Enterprise POC:**
• Extended 30-day pilot
• Dedicated solutions engineer
• Custom success metrics
• Full compliance review

**Book Now:** [Schedule Demo](/contact) or email sales@vivyawatch.com

We'll show you exactly how VivyaWatch can work for your team.`,

  support: `VivyaWatch support options:

**All Plans Include:**
• Email support (24-hour response)
• In-app live chat (business hours)
• Knowledge base at docs.vivyawatch.com
• Video tutorials & webinars
• Community forum

**Professional Plan Adds:**
• Priority email (8-hour response)
• Onboarding assistance
• Quarterly business reviews

**Enterprise Plan Adds:**
• Dedicated Customer Success Manager
• 4-hour SLA for critical issues
• Phone support
• Custom training sessions
• Slack/Teams integration for support

**Contact:**
• Email: support@vivyawatch.com
• Sales: sales@vivyawatch.com
• Security: security@vivyawatch.com`,

  trial: `Start your free trial today:

**What's Included:**
• **14 days full access** - no credit card required
• **Up to 25 users** during trial
• **All features unlocked** including AI insights
• **Free onboarding call** with our team
• **No obligations** - cancel anytime

**Getting Started:**
1. Sign up at [vivyawatch.com/signup](/signup)
2. Download agents for your devices
3. Start monitoring in minutes
4. Schedule onboarding call

**Enterprise Trials:**
• Extended 30-day POC available
• Custom user limits
• Dedicated solutions engineer
• Compliance review included

[Start Free Trial](/pricing) or [Talk to Sales](/contact) for enterprise trials.`,

  // Fallback for general questions
  general: `Thanks for your question! Here's what VivyaWatch offers:

**VivyaWatch** is a privacy-first employee monitoring and workforce intelligence platform.

**Core Capabilities:**
• Real-time productivity analytics
• Screenshot intelligence with AI
• Insider risk detection (UEBA)
• Compliance suite (HIPAA, GDPR, SOC 2)

**Quick Links:**
• [View Pricing](/pricing) - Starting at $5/user/month
• [Book a Demo](/contact) - 30-minute personalized walkthrough
• [Start Free Trial](/signup) - 14 days, no credit card

What specific aspect would you like to know more about? I can help with:
- Pricing & plans
- Security & compliance (HIPAA, GDPR, SOC 2)
- Features & capabilities
- Deployment & integration
- How we compare to alternatives`,

  about: `**About VivyaWatch**

VivyaWatch is a **privacy-first employee monitoring and workforce intelligence platform** designed for modern enterprises.

**Our Mission:**
To help organizations balance productivity visibility with employee privacy and trust.

**What Makes Us Different:**
• **Privacy-First**: Built-in consent management, transparency portal, DPIA automation
• **AI-Powered**: GPT-4 powered insights, natural language queries
• **Compliance-Ready**: HIPAA, GDPR, SOC 2, ISO 27001
• **Fair Pricing**: No minimum users, transparent pricing starting at $5/user/month

**Trusted By:**
• 500+ companies worldwide
• Healthcare, finance, technology, and professional services
• Teams from 10 to 10,000+ employees

**Founded:** 2023 | **Headquarters:** [Location]

[Learn More About Us](/about) | [Contact Sales](/contact)`,

  // Industry-specific
  industries: `VivyaWatch serves diverse industries:

**Healthcare:**
• HIPAA-compliant monitoring
• PHI protection and redaction
• Audit trails for compliance
• BAA support included

**Financial Services:**
• SOC 2 & ISO 27001 certified
• Insider threat detection
• Data loss prevention (DLP)
• Regulatory audit support

**Legal & Professional Services:**
• Client confidentiality protection
• Billable hours tracking
• Attorney-client privilege zones
• Document access monitoring

**Technology & SaaS:**
• Developer-friendly deployment
• API-first architecture
• Source code protection
• Remote team optimization

**BPO & Call Centers:**
• Real-time productivity dashboards
• Quality assurance integration
• Multi-shift support
• Performance benchmarking

**Retail & Manufacturing:**
• POS system monitoring
• Shift-based tracking
• Multi-location support

[See Industry Solutions](/industries) | [Request Industry Demo](/contact)`,

  // Insider threat detection
  insider_threat: `VivyaWatch provides advanced insider threat detection:

**User & Entity Behavior Analytics (UEBA):**
• Machine learning-based anomaly detection
• Baseline behavior profiling per user
• Risk scoring (0-100) for each user
• Real-time alerts for suspicious activity

**Detection Capabilities:**
• **Data Exfiltration**: Large file transfers, USB usage, cloud uploads
• **Fake Activity**: Mouse jigglers, fake engagement patterns
• **Policy Violations**: Unauthorized app usage, after-hours access
• **Flight Risk**: Patterns indicating employee departure
• **Credential Misuse**: Privilege escalation, shared credentials

**Alert & Response:**
• Real-time Slack/Teams/email alerts
• SIEM integration (Splunk, Sentinel)
• Automated response playbooks
• Incident investigation timeline
• Forensic evidence preservation

**Privacy-Balanced:**
• Alert on risk, not micro-manage
• Configurable sensitivity levels
• HR/Legal review workflows
• False positive reduction AI

[Learn About Insider Risk](/insider-risk) | [Request Security Demo](/contact)`,

  // Reports and analytics
  reports: `VivyaWatch offers comprehensive reporting:

**Pre-Built Reports:**
• Productivity Summary (daily/weekly/monthly)
• Application Usage Analysis
• Website Category Breakdown
• Active Time vs Idle Time
• Team & Individual Comparisons
• Trend Analysis Over Time

**Compliance Reports:**
• Audit Trail Exports
• User Access Logs
• Policy Violation History
• Data Retention Compliance
• HIPAA/GDPR Ready Reports

**Executive Dashboards:**
• Organization-wide productivity
• Department comparisons
• Cost savings metrics
• ROI calculations

**Custom Reports:**
• Drag-and-drop report builder
• Custom date ranges
• Filter by team/user/application
• Schedule automated delivery
• Export to PDF, CSV, Excel

**AI-Powered Insights:**
• Natural language queries
• Trend predictions
• Anomaly highlights
• Smart recommendations

[View Report Samples](/reports) | [Request Demo](/contact)`,

  // Legal considerations
  legal: `VivyaWatch supports legal compliance for employee monitoring:

**Legal Framework Support:**
• ECPA (Electronic Communications Privacy Act) compliant
• CCPA (California Consumer Privacy Act) ready
• State-specific privacy law guidance
• International labor law considerations

**Required Disclosures:**
• Customizable consent forms
• Employee notification templates
• Policy acknowledgment tracking
• Multi-language support

**HR Best Practices:**
• Clear monitoring policy templates
• Manager training resources
• Employee communication guides
• Union consultation considerations

**Documentation:**
• Audit-ready records
• Policy change history
• Consent timestamps
• Investigation evidence preservation

**Important Notes:**
• Laws vary by jurisdiction - consult legal counsel
• We provide tools, not legal advice
• Employee consent requirements differ by location
• Some jurisdictions require works council approval

[Download Legal Guide](/legal) | [Contact Compliance Team](/contact)`,

  // ROI and business value
  roi: `VivyaWatch delivers measurable business value:

**Typical ROI Metrics:**
• **15-25% productivity increase** in first 90 days
• **$3,400/employee/year** in productivity gains
• **40% reduction** in insider threat incidents
• **60% faster** investigation time for incidents
• **80% reduction** in time theft and buddy punching

**Cost Savings Examples:**
• 100 employees × $3,400 = **$340,000/year** productivity gain
• VivyaWatch cost: ~$10,800/year (at $9/user/month)
• **ROI: 3,000%+**

**Qualitative Benefits:**
• Identify top performers for recognition
• Data-driven performance reviews
• Remote work accountability
• Reduced compliance risk
• Better resource allocation

**Case Studies:**
• [Tech Startup] - 22% productivity increase
• [Healthcare Org] - HIPAA compliance in 2 weeks
• [BPO Company] - 18% reduction in overtime costs

[View ROI Calculator](/roi) | [Read Case Studies](/case-studies)`,

  // Stealth vs visible monitoring
  stealth: `VivyaWatch supports both visible and stealth monitoring:

**Visible Mode (Recommended):**
• Employee tray icon shows monitoring status
• "Currently Tracked" indicator
• Builds trust and transparency
• Recommended for most organizations
• Improves productivity through awareness

**Stealth Mode (Enterprise Only):**
• No visible indication of monitoring
• Background operation
• Available for specific use cases:
  - Active investigations
  - Insider threat response
  - Legal/compliance requirements
• Requires legal justification
• Additional compliance review needed

**Hybrid Approach:**
• Visible by default
• Stealth for specific investigations
• Manager approval workflow
• Time-limited stealth periods
• Full audit trail

**Important Considerations:**
• Check local laws before using stealth mode
• Some jurisdictions require notification
• We recommend visible mode for trust
• Legal documentation required for stealth

[Learn About Monitoring Modes](/docs/modes) | [Talk to Compliance](/contact)`,

  // Mobile monitoring
  mobile: `VivyaWatch mobile monitoring capabilities:

**Mobile App Monitoring:**
• Android agent available
• iOS monitoring (MDM-based)
• Location tracking (opt-in)
• App usage analytics
• Call/SMS logging (where legal)

**BYOD Considerations:**
• Work profile separation (Android)
• Privacy zones for personal apps
• Employee consent workflows
• Clear work/personal boundaries

**MDM Integration:**
• Microsoft Intune
• Jamf (for iOS/macOS)
• VMware Workspace ONE
• MobileIron

**Mobile-Specific Features:**
• GPS location history
• Working hours geofencing
• Mobile app categorization
• Battery-optimized agent

**Note:** Mobile monitoring has additional legal requirements in many jurisdictions. Consult legal counsel and obtain clear employee consent.

[Mobile Monitoring Guide](/docs/mobile) | [Request Demo](/contact)`,

  // Time tracking specifics
  timetracking: `VivyaWatch time tracking features:

**Automatic Time Tracking:**
• First keystroke/mouse start
• Idle detection (configurable threshold)
• Break time tracking
• Overtime calculations
• Project/task attribution

**Time Categories:**
• Active work time
• Idle time
• Break time (scheduled)
• Focus time (deep work)
• Meeting time (calendar integration)

**Attendance Features:**
• Clock in/out tracking
• Timesheet automation
• PTO and leave tracking
• Shift scheduling
• Geolocation check-in (mobile)

**Billable Hours:**
• Project-based tracking
• Client attribution
• Export for invoicing
• Integration with billing tools

**Fairness Features:**
• Excludes personal break time
• Configurable work hours
• No after-hours tracking (default)
• Timezone-aware calculations

[Explore Time Tracking](/features/time-tracking) | [Start Free Trial](/signup)`
};

// Generate session ID
const generateSessionId = () => `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const ConversationalChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId] = useState(generateSessionId);
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  // Auto-popup state - shows after 5 seconds on landing page
  const [showAutoPopup, setShowAutoPopup] = useState(false);
  const [autoPopupDismissed, setAutoPopupDismissed] = useState(false);

  // Analytics state
  const [topicsDiscussed, setTopicsDiscussed] = useState([]);
  const [intentScore, setIntentScore] = useState(0);
  const [questionsAsked, setQuestionsAsked] = useState([]);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Use react-router location so the check re-runs on every route change
  const location = useLocation();

  // Compute synchronously (not in useEffect) to avoid the one-render flash where
  // shouldShow is still `true` from a previous public page while the current
  // pathname is already /signin, /dashboard, etc.  That flash was causing a
  // production crash because the chat widget briefly rendered on auth pages.
  const publicPaths = ['/', '/pricing', '/features', '/solutions', '/resources', '/partner', '/contact', '/privacy-policy', '/security-compliance', '/screenshot-monitoring', '/productivity-analytics', '/compliance-suite', '/insider-risk', '/tryus', '/compare', '/deployment-guide'];
  const _pathname = location.pathname;
  const shouldShow = !(
    _pathname.includes('/signin') ||
    _pathname.includes('/signup') ||
    _pathname.includes('/login') ||
    _pathname.includes('/super-admin') ||
    _pathname.includes('/dashboard') ||
    _pathname.startsWith('/employee-') ||
    _pathname.startsWith('/billing') ||
    _pathname.startsWith('/settings') ||
    _pathname.startsWith('/alerts') ||
    _pathname.startsWith('/setup') ||
    _pathname.startsWith('/onboarding') ||
    _pathname.startsWith('/sso') ||
    _pathname.startsWith('/password-') ||
    _pathname.startsWith('/profile') ||
    _pathname.startsWith('/device-consent')
  ) && (
    publicPaths.includes(_pathname) ||
    _pathname === '/' ||
    _pathname.startsWith('/tryus') ||
    _pathname.startsWith('/features/') ||
    _pathname.startsWith('/deployment-guide') ||
    _pathname.startsWith('/resources/')
  );

  // Initialize with welcome message
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        id: 1,
        type: 'bot',
        content: "Hi!  I'm VivyaWatch's AI assistant. Ask me anything about employee monitoring, productivity analytics, security, or pricing. No email needed - just ask!",
        timestamp: new Date()
      }]);
    }
  }, [messages.length]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  // Auto-popup after 5 seconds on landing page (like Teramind)
  useEffect(() => {
    // Only show auto-popup on public pages, if not already dismissed, and chat is not open
    if (!shouldShow || autoPopupDismissed || isOpen) {
      return;
    }

    // Check if user has already seen the popup in this session
    const hasSeenPopup = sessionStorage.getItem('vivya_chat_popup_seen');
    if (hasSeenPopup) {
      return;
    }

    const timer = setTimeout(() => {
      setShowAutoPopup(true);
      // Track the popup display
      analyticsService.track('Chat Auto-Popup Shown', { page: window.location.pathname });
    }, 5000); // 5 seconds delay

    return () => clearTimeout(timer);
  }, [shouldShow, autoPopupDismissed, isOpen]);

  // Hide auto-popup when chat is opened
  useEffect(() => {
    if (isOpen && showAutoPopup) {
      setShowAutoPopup(false);
      sessionStorage.setItem('vivya_chat_popup_seen', 'true');
    }
  }, [isOpen, showAutoPopup]);

  // Handle dismissing the auto-popup
  const dismissAutoPopup = () => {
    setShowAutoPopup(false);
    setAutoPopupDismissed(true);
    sessionStorage.setItem('vivya_chat_popup_seen', 'true');
    analyticsService.track('Chat Auto-Popup Dismissed', { page: window.location.pathname });
  };

  // Handle clicking the auto-popup to open chat
  const handleAutoPopupClick = () => {
    setShowAutoPopup(false);
    sessionStorage.setItem('vivya_chat_popup_seen', 'true');
    setIsOpen(true);
    analyticsService.track('Chat Auto-Popup Clicked', { page: window.location.pathname });
  };

  // Detect topics from message with priority ordering
  const detectTopics = useCallback((text) => {
    const lowered = text.toLowerCase();
    const detected = [];

    // Check topics in priority order
    for (const topic of TOPIC_PRIORITY) {
      const keywords = TOPIC_KEYWORDS[topic];
      if (keywords && keywords.some(kw => lowered.includes(kw))) {
        detected.push(topic);
      }
    }

    return detected.length > 0 ? detected : ['general'];
  }, []);

  // Calculate intent score based on behavior
  const calculateIntentScore = useCallback((newTopics, messageCount) => {
    let score = 0;
    
    // More messages = higher intent
    score += Math.min(messageCount * 10, 40);
    
    // High-value topics
    if (newTopics.includes('pricing')) score += 20;
    if (newTopics.includes('deployment')) score += 15;
    if (newTopics.includes('comparison')) score += 15;
    if (newTopics.includes('security')) score += 10;
    
    // Multiple topics = serious evaluation
    if (newTopics.length >= 3) score += 15;
    
    return Math.min(score, 100);
  }, []);

  // Get AI response (grounded in knowledge base, falls back to API)
  const getAIResponse = useCallback(async (userMessage) => {
    const topics = detectTopics(userMessage);
    
    // Check knowledge base first
    for (const topic of topics) {
      if (KNOWLEDGE_BASE[topic]) {
        return {
          content: KNOWLEDGE_BASE[topic],
          topics,
          fromKnowledgeBase: true
        };
      }
    }
    
    // Fall back to AI API for complex questions
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/chat/conversation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          sessionId,
          context: {
            topicsDiscussed,
            questionsAsked: questionsAsked.slice(-5),
            intentScore
          }
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        return {
          content: data.response,
          topics,
          fromKnowledgeBase: false
        };
      }
    } catch (error) {
      console.error('Chat API error:', error);
    }
    
    // Fallback response
    return {
      content: "I'd be happy to help with that! For detailed information, you can:\n\n• Check our [Features page](/features)\n• View [Pricing](/pricing)\n• [Contact our team](/contact) for a personalized demo\n\nOr ask me something more specific!",
      topics: ['general'],
      fromKnowledgeBase: false
    };
  }, [detectTopics, sessionId, topicsDiscussed, questionsAsked, intentScore]);

  // Handle sending a message
  const handleSend = async () => {
    if (!inputValue.trim()) return;

    const userMessage = inputValue.trim();
    setInputValue('');

    // Add user message
    const userMsg = {
      id: Date.now(),
      type: 'user',
      content: userMessage,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMsg]);

    // Track question
    const newQuestions = [...questionsAsked, userMessage];
    setQuestionsAsked(newQuestions);

    // Detect topics
    const detectedTopics = detectTopics(userMessage);
    const allTopics = [...new Set([...topicsDiscussed, ...detectedTopics])];
    setTopicsDiscussed(allTopics);

    // Calculate intent score
    const newScore = calculateIntentScore(allTopics, newQuestions.length);
    setIntentScore(newScore);

    // Track in analytics (anonymous)
    analyticsService.track('Chat Message Sent', {
      sessionId,
      topics: detectedTopics,
      messageCount: newQuestions.length,
      intentScore: newScore,
      anonymous: true
    });

    // Show typing indicator
    setIsTyping(true);

    // Get AI response
    const aiResponse = await getAIResponse(userMessage);

    // Add slight delay for natural feel
    await new Promise(resolve => setTimeout(resolve, 500 + Math.random() * 1000));

    setIsTyping(false);

    // Add bot response
    const botMsg = {
      id: Date.now() + 1,
      type: 'bot',
      content: aiResponse.content,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, botMsg]);

    // Show email capture after 3+ valuable exchanges (not forced)
    if (newQuestions.length >= 3 && !showEmailCapture && !emailSubmitted) {
      setTimeout(() => {
        setShowEmailCapture(true);
      }, 2000);
    }
  };

  // Handle email submission
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await fetch(`${backendUrl}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          source: 'conversational_chat',
          page: window.location.pathname,
          visitorId: analyticsService.visitorId,
          leadScore: intentScore,
          visitorData: {
            sessionId,
            topicsDiscussed,
            questionsAsked,
            messageCount: questionsAsked.length,
            intentScore
          }
        })
      });

      analyticsService.identify(email, {
        chatSessionId: sessionId,
        topicsDiscussed,
        intentScore
      });

      setEmailSubmitted(true);
      setShowEmailCapture(false);

      // Add confirmation message
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'bot',
        content: "Thanks! I'll send you a summary of our conversation. Feel free to keep asking questions! ",
        timestamp: new Date()
      }]);

    } catch (error) {
      console.error('Email capture error:', error);
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Toggle chat open/close
  const toggleChat = () => {
    if (!isOpen) {
      analyticsService.track('Chat Opened', { page: window.location.pathname });
    }
    setIsOpen(!isOpen);
    setIsMinimized(false);
  };

  // Render markdown-like content
  const renderContent = (content) => {
    // Simple markdown rendering
    return content.split('\n').map((line, i) => {
      // Bold
      line = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      // Links
      line = line.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-orange-600 hover:underline">$1</a>');

      // Sanitize HTML to prevent XSS attacks
      const sanitizedLine = DOMPurify.sanitize(line, {
        ALLOWED_TAGS: ['strong', 'a', 'b', 'i', 'em', 'code', 'pre', 'br'],
        ALLOWED_ATTR: ['href', 'class'],
        ALLOW_DATA_ATTR: false
      });

      // Bullets
      if (line.startsWith('• ') || line.startsWith('- ')) {
        return <div key={i} className="ml-4" dangerouslySetInnerHTML={{ __html: sanitizedLine }} />;
      }
      return <div key={i} dangerouslySetInnerHTML={{ __html: sanitizedLine }} />;
    });
  };

  // Don't render on non-public pages (signin, signup, dashboard, etc.)
  if (!shouldShow) return null;

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          onClick={toggleChat}
          className="fixed bottom-6 right-6 z-50 p-4 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 group"
          style={{
            background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
            boxShadow: '0 8px 32px rgba(249, 115, 22, 0.4)'
          }}
          aria-label="Open chat"
        >
          <MessageCircle className="w-6 h-6 text-white" />
          {/* Pulse animation - show notification badge when auto-popup is visible */}
          <span className={`absolute -top-1 -right-1 w-4 h-4 rounded-full animate-pulse ${showAutoPopup ? 'bg-red-500' : 'bg-green-500'}`} aria-hidden="true">
            {showAutoPopup && <span className="absolute inset-0 flex items-center justify-center text-white text-[10px] font-bold">1</span>}
          </span>
          <span className="sr-only">{showAutoPopup ? 'New notification' : 'No notifications'}</span>
          {/* Tooltip - hide when auto-popup is showing */}
          {!showAutoPopup && (
            <span className="absolute bottom-full right-0 mb-2 px-3 py-1 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
              Ask me anything!
            </span>
          )}
        </button>
      )}

      {/* Auto-Popup Message Bubble - Teramind style */}
      {!isOpen && showAutoPopup && (
        <div
          className="fixed bottom-40 right-6 z-50 animate-slideInRight"
          style={{ maxWidth: '340px' }}
        >
          {/* Main message bubble */}
          <div
            className="bg-white rounded-2xl p-5 mb-3 cursor-pointer transition-all relative border border-gray-100"
            onClick={handleAutoPopupClick}
            style={{
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.12), 0 8px 20px rgba(249, 115, 22, 0.08)'
            }}
          >
            {/* Close button */}
            <button
              onClick={(e) => { e.stopPropagation(); dismissAutoPopup(); }}
              className="absolute -top-2 -right-2 w-7 h-7 bg-white hover:bg-gray-50 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 dark:text-gray-200 transition-colors shadow-lg border border-gray-100"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-start gap-4">
              {/* VivyaWatch logo/icon */}
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg"
                style={{ background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' }}
              >
                <span className="text-white font-bold text-lg">V</span>
              </div>

              <div className="flex-1 pt-0.5">
                <p className="text-gray-800 text-sm leading-relaxed font-medium">
                   Hi there! Want to explore VivyaWatch?
                </p>
                <p className="text-gray-600 dark:text-gray-200 text-sm mt-2 leading-relaxed">
                  Check out our{' '}
                  <a
                    href="/tryus"
                    className="text-orange-600 font-semibold hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    interactive demo
                  </a>
                  {' '}or ask me anything about employee monitoring!
                </p>
                <p className="text-gray-500 dark:text-gray-300 text-xs mt-2 flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" aria-hidden="true"></span>
                  VivyaWatch AI • Online now
                </p>
              </div>
            </div>
          </div>

          {/* "Speak with a member" button */}
          <button
            onClick={handleAutoPopupClick}
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-3.5 px-4 rounded-xl shadow-lg transition-all hover:shadow-xl text-sm hover:scale-[1.02] active:scale-[0.98]"
          >
             Chat with our team
          </button>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className={`fixed z-50 transition-all duration-300 ${
            isMinimized
              ? 'bottom-6 right-6 w-72 h-14'
              : 'bottom-6 right-6 w-96 h-[600px] max-h-[80vh]'
          }`}
          style={{
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}
        >
          <div
            className="h-full flex flex-col rounded-2xl overflow-hidden"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.3)'
            }}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-4 py-3 cursor-pointer"
              style={{
                background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)'
              }}
              onClick={() => isMinimized && setIsMinimized(false)}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white text-sm">VivyaWatch AI</h3>
                  <p className="text-white/80 text-xs">
                    {isTyping ? 'Typing...' : 'Online • No email required'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  aria-label={isMinimized ? 'Maximize' : 'Minimize'}
                >
                  {isMinimized ? (
                    <Maximize2 className="w-4 h-4 text-white" />
                  ) : (
                    <Minimize2 className="w-4 h-4 text-white" />
                  )}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  aria-label="Close chat"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>

            {/* Messages Area */}
            {!isMinimized && (
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ backgroundColor: '#f8fafc' }}>
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                          msg.type === 'user'
                            ? 'bg-orange-500 text-white rounded-br-sm'
                            : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-sm'
                        }`}
                      >
                        <div className="text-sm leading-relaxed">
                          {msg.type === 'bot' ? renderContent(msg.content) : msg.content}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Typing indicator */}
                  {isTyping && (
                    <div className="flex justify-start">
                      <div className="bg-white rounded-2xl px-4 py-3 shadow-sm border border-gray-100">
                        <div className="flex gap-1" role="status">
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" aria-hidden="true" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" aria-hidden="true" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" aria-hidden="true" style={{ animationDelay: '300ms' }} />
                          <span className="sr-only">Loading</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Email capture offer (non-intrusive) */}
                  {showEmailCapture && !emailSubmitted && (
                    <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <Mail className="w-5 h-5 text-orange-600 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-800 mb-2">
                            Want this conversation emailed to you?
                          </p>
                          <form onSubmit={handleEmailSubmit} className="flex gap-2">
                            <input
                              type="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              placeholder="Your email (optional)"
                              className="flex-1 px-3 py-2 text-sm border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                              aria-label="Your email"
                            />
                            <button
                              type="submit"
                              className="px-3 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors"
                            >
                              Send
                            </button>
                          </form>
                          <button
                            onClick={() => setShowEmailCapture(false)}
                            className="text-xs text-gray-500 dark:text-gray-300 mt-2 hover:text-gray-700"
                          >
                            No thanks, continue chatting
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="p-4 border-t border-gray-100" style={{ backgroundColor: '#ffffff' }}>
                  <div className="flex items-center gap-2">
                    <input
                      ref={inputRef}
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Ask about pricing, security, features..."
                      className="flex-1 px-4 py-3 bg-gray-50 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 focus:bg-white outline-none transition-all"
                      disabled={isTyping}
                      aria-label="Chat message"
                    />
                    <button
                      onClick={handleSend}
                      disabled={!inputValue.trim() || isTyping}
                      className="p-3 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        background: inputValue.trim() ? 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' : '#e5e7eb'
                      }}
                    >
                      <Send className={`w-5 h-5 ${inputValue.trim() ? 'text-white' : 'text-gray-500 dark:text-gray-300'}`} />
                    </button>
                  </div>

                  {/* Intent score indicator (subtle) */}
                  {intentScore > 50 && (
                    <div className="mt-2 flex items-center justify-center gap-2">
                      <span className="text-xs text-gray-500 dark:text-gray-300">High interest detected</span>
                      <Sparkles className="w-3 h-3 text-orange-400" />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Animation styles for auto-popup */}
      <style>{`
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-slideInRight {
          animation: slideInRight 0.3s ease-out forwards;
        }
      `}</style>
    </>
  );
};

export default ConversationalChat;
